import { useRef } from "react";
import { useForm } from "react-hook-form";
import { Button, Checkbox, Form } from "@telluscompany/design-system";

import { useLocalization } from "@i18n";
import { buildAssetUrl } from "@shared/utils";
import { useAcceptDocumentMutation } from "@shared/api/server/gs3Server";
import type {
  LegalDocumentsType,
  PendingDocument,
} from "@shared/api/server/gs3Server";

import { Viewer } from "@react-pdf-viewer/core";

interface AcceptFormValues {
  accepted: boolean;
}

interface LegalDocumentModalProps {
  document: PendingDocument;
  onAccepted: () => void;
  onlyView?: boolean;
}

export const LegalDocumentModal = ({
  document,
  onAccepted,
  onlyView = false,
}: LegalDocumentModalProps) => {
  const { translate } = useLocalization();
  const containerRef = useRef<HTMLDivElement>(null);

  const { mutate, isPending } = useAcceptDocumentMutation();

  const docType = document.type as LegalDocumentsType;
  const fileUrl = buildAssetUrl(document.storagePath);

  const form = useForm<AcceptFormValues>({
    defaultValues: { accepted: false },
  });
  const accepted = form.watch("accepted");

  const handleAccept = () => {
    mutate(document.id, { onSuccess: onAccepted });
  };

  return (
    <div className="bg-background rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
      <div className="px-6 pt-6 pb-4 border-b border-border">
        <h5 className="text-h5">
          {translate(`legalDocuments.modal.${docType}.title`)}
        </h5>
        <p className="text-sm text-muted-foreground mt-1">
          {translate("legalDocuments.modal.version")} {document.version}
        </p>
      </div>

      <div ref={containerRef} className="flex-1 overflow-y-auto px-6 py-4">
        <Viewer fileUrl={fileUrl} defaultScale={1.5} />
      </div>

      {!onlyView && (
        <Form {...form}>
          <div className="px-6 py-4 border-t border-border flex justify-between items-center align-middle [&_[aria-live]]:min-h-0">
            <Checkbox
              control={form.control}
              name="accepted"
              label={translate(`legalDocuments.modal.${docType}.readConfirm`)}
            />
            <div className="flex justify-end">
              <Button
                variant="primary"
                disabled={!accepted || isPending}
                onClick={handleAccept}
              >
                {translate("legalDocuments.modal.accept")}
              </Button>
            </div>
          </div>
        </Form>
      )}
    </div>
  );
};
